import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dog,
  Cat,
  Plus,
  Star,
  PawPrint,
  MessageSquare,
  BarChart3,
  Award,
  ExternalLink,
  MapPin,
  BarChart2,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { AnimalActionMenu } from "@/components/animal-action-menu";
import { MetricWidget } from "@/components/metrics/MetricWidget";
import type { CapacityStatus, LocationSummaryData } from "@/hooks/use-capacity";
import { useLengthOfStay } from "@/hooks/use-population-insights";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { computeDaysInShelter } from "@/lib/animal-utils";
import { useReportFreshness } from "@/hooks/use-report-freshness";


interface LocationData {
  location: string;
  locationLabel: string;
  total: number;
  dogs: number;
  cats: number;
  trend: number;
  lastReportDate: string | null;
  lastUploadedAt: string | null;
  generatedAt?: { timestamp: string | null; date: string; confidence: 'exact' | 'date_only' } | null;
}

interface LocationMetric {
  id: string;
  label: string;
  description?: string | null;
  displayFields?: string[];
  defaultSortField?: string | null;
  defaultSortDirection?: string | null;
  chartEnabled?: boolean;
  defaultView?: string;
  resultType?: string;
}

interface LocationStripProps {
  locations: LocationData[];
  capacityByLocation: Record<string, LocationSummaryData>;
  onViewAnimalPreview: (animalId: string) => void;
  onAddAnimalTo: (animalId: string, name?: string, species?: string, photoUrl?: string | null) => void;
  onLocationExpanded?: (location: string | null) => void;
  initialLocation?: string;
  sessionLocationCodes?: string[];
  canToggleLocationScope: boolean;
  showAllLocations: boolean;
  onToggleAllLocations: () => void;
  sortedLocationsCount: number;
}

function getCapacityBarColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "bg-destructive";
    case "near":
      return "bg-amber-500 dark:bg-amber-600";
    case "ok":
      return "bg-emerald-500 dark:bg-emerald-600";
    default:
      return "bg-muted";
  }
}

function getCapacityTextColor(status: CapacityStatus): string {
  switch (status) {
    case "over":
      return "text-destructive";
    case "near":
      return "text-amber-600 dark:text-amber-500";
    case "ok":
      return "text-emerald-600 dark:text-emerald-500";
    default:
      return "text-muted-foreground";
  }
}

function AdoptionLevelBar({ level, count, total, color }: { level: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-mono font-bold text-muted-foreground w-6">{level}</span>
      <div className="flex-1 bg-secondary rounded-full h-5 relative overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-foreground/70">{count}</span>
      </div>
      <span className="text-[10px] text-muted-foreground w-8 text-right">{pct}%</span>
    </div>
  );
}

interface AnimalListItem {
  animalId: string;
  name: string;
  species: string;
  photoUrl: string | null;
  adoptionLevelNorm: string | null;
  intakeDate: string | null;
}

interface AdoptionPipelineLatest {
  L1: number;
  L2: number;
  L3: number;
  L4: number;
  unknown: number;
  total: number;
}

interface IntakeOutcomeTrend {
  date: string;
  intakes: number;
  outcomes: number;
}

export function LocationStrip({
  locations,
  capacityByLocation,
  onViewAnimalPreview,
  onAddAnimalTo,
  onLocationExpanded,
  initialLocation,
  sessionLocationCodes,
  canToggleLocationScope,
  showAllLocations,
  onToggleAllLocations,
  sortedLocationsCount,
}: LocationStripProps) {
  const [expandedLocation, setExpandedLocation] = useState<string | null>(null);
  const [defaultLocation, setDefaultLocation] = useState<string | null>(null);
  const hasHydrated = useRef(false);

  const queryClient = useQueryClient();
  const starMutation = useMutation({
    mutationFn: async (newDefault: string | null) => {
      return apiRequest("PATCH", "/api/account/default-location", {
        defaultLocationCode: newDefault,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/account/locations"] });
    },
  });

  useEffect(() => {
    if (hasHydrated.current || locations.length === 0) return;
    hasHydrated.current = true;
    if (initialLocation && locations.some(l => l.location === initialLocation)) {
      setDefaultLocation(initialLocation);
      setExpandedLocation(initialLocation);
      onLocationExpanded?.(initialLocation);
    } else {
      setExpandedLocation(locations[0].location);
      onLocationExpanded?.(locations[0].location);
    }
  }, [locations]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetDefault = (locCode: string) => {
    const newDefault = defaultLocation === locCode ? null : locCode;
    setDefaultLocation(newDefault);
    starMutation.mutate(newDefault);
  };

  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleToggleLocation = useCallback((locCode: string) => {
    if (expandedLocation === locCode) return;
    setExpandedLocation(locCode);
    onLocationExpanded?.(locCode);
    setTimeout(() => {
      cardRefs.current[locCode]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [expandedLocation, onLocationExpanded]);

  const handleSelectFromSwitcher = useCallback((locCode: string) => {
    if (expandedLocation === locCode) return;
    setExpandedLocation(locCode);
    onLocationExpanded?.(locCode);
  }, [expandedLocation, onLocationExpanded]);

  const isMobile = useIsMobile();
  const expandedLoc = locations.find(l => l.location === expandedLocation) ?? null;

  useEffect(() => {
    if (!hasHydrated.current) return;
    if (expandedLocation !== null && expandedLoc === null && locations.length > 0) {
      setExpandedLocation(locations[0].location);
      onLocationExpanded?.(locations[0].location);
    }
  }, [expandedLocation, locations]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: locationMetricsData } = useQuery<{ metrics: LocationMetric[] }>({
    queryKey: ["/api/metrics", "location_snapshot"],
    queryFn: async () => {
      const res = await fetch("/api/metrics?target=location_snapshot", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch location metrics");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });
  const locationMetrics: LocationMetric[] = (locationMetricsData?.metrics ?? []).filter(
    (m: LocationMetric & { status?: string }) => m.status === "published",
  );

  return (
    <div className="space-y-3" data-tour="dashboard-location-strip">
      {!isMobile && locations.length !== 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {locations.map((loc) => {
            const isExpanded = expandedLocation === loc.location;
            const isDefault = defaultLocation === loc.location;
            const capacity = capacityByLocation[loc.location];
            const utilizationPercent = capacity?.utilizationConfigured ?? null;
            const status = capacity?.status ?? "unknown";

            return (
              <div
                key={loc.location}
                ref={(el) => { cardRefs.current[loc.location] = el; }}
                role="button"
                tabIndex={0}
                onClick={() => handleToggleLocation(loc.location)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleToggleLocation(loc.location); }}
                className={`flex items-center gap-3 px-4 py-5 text-left w-full rounded-md border cursor-pointer hover-elevate active-elevate-2 scroll-mt-4 ${
                  isExpanded
                    ? "border-primary ring-1 ring-primary/20"
                    : "border-border"
                }`}
                data-testid={`button-location-${loc.location}`}
                data-tour="dashboard-location-card"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span className={`font-bold text-base tracking-tight uppercase ${!isExpanded ? "text-muted-foreground" : ""}`}>
                        {loc.locationLabel}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); handleSetDefault(loc.location); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); handleSetDefault(loc.location); } }}
                        aria-label={isDefault ? "Remove as default location" : "Set as default location"}
                        className="cursor-pointer flex-shrink-0"
                        data-testid={`button-star-${loc.location}`}
                      >
                        <Star className={`h-3.5 w-3.5 ${isDefault ? "text-amber-500 fill-amber-500" : "text-muted-foreground"}`} />
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-lg font-medium ${!isExpanded ? "text-muted-foreground" : "text-foreground/90 dark:text-foreground"}`} data-testid={`text-location-total-${loc.location}`}>
                        {loc.total}
                      </span>
                      <div className="flex gap-1 text-muted-foreground">
                        <Dog className="h-3.5 w-3.5" />
                        <span className="text-[11px] font-medium">{loc.dogs}</span>
                        <Cat className="h-3.5 w-3.5 ml-1" />
                        <span className="text-[11px] font-medium">{loc.cats}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <div className="flex-1 relative h-2.5 overflow-hidden rounded-full bg-secondary">
                      <div
                        className={`h-full transition-all rounded-full ${!isExpanded ? "bg-input" : getCapacityBarColor(status)}`}
                        style={{ width: `${Math.min(utilizationPercent ?? 0, 100)}%` }}
                      />
                    </div>
                    {utilizationPercent !== null && (
                      <span className={`text-xs font-bold ${!isExpanded ? "text-muted-foreground" : getCapacityTextColor(status)}`} data-testid={`text-capacity-${loc.location}`}>
                        {utilizationPercent}%
                      </span>
                    )}
                    {!isExpanded && <Plus className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {expandedLoc && (
        <LocationDetailPanel
          locationCode={expandedLoc.location}
          locationLabel={expandedLoc.locationLabel}
          lastReportDate={expandedLoc.lastReportDate}
          locationData={expandedLoc}
          capacity={capacityByLocation[expandedLoc.location] ?? null}
          locationMetrics={locationMetrics}
          onViewAnimalPreview={onViewAnimalPreview}
          onAddAnimalTo={onAddAnimalTo}
          allLocations={locations}
          onSelectLocation={handleSelectFromSwitcher}
          defaultLocationCode={defaultLocation}
          canToggleLocationScope={canToggleLocationScope}
          showAllLocations={showAllLocations}
          onToggleAllLocations={onToggleAllLocations}
          sortedLocationsCount={sortedLocationsCount}
          allCapacity={capacityByLocation}
        />
      )}
    </div>
  );
}

interface LocationDetailPanelProps {
  locationCode: string;
  locationLabel: string;
  lastReportDate: string | null;
  locationData: LocationData | null;
  capacity: LocationSummaryData | null;
  locationMetrics: LocationMetric[];
  onViewAnimalPreview: (animalId: string) => void;
  onAddAnimalTo: (animalId: string, name?: string, species?: string, photoUrl?: string | null) => void;
  allLocations: LocationData[];
  onSelectLocation: (locCode: string) => void;
  defaultLocationCode: string | null;
  canToggleLocationScope: boolean;
  showAllLocations: boolean;
  onToggleAllLocations: () => void;
  sortedLocationsCount: number;
  allCapacity: Record<string, LocationSummaryData>;
}

function formatReportDate(dateString: string | null): string {
  if (!dateString) return "—";
  try {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  } catch {
    return "—";
  }
}

// Task #99: Format generatedAt for badge display
function formatGeneratedAtBadge(generatedAt: { timestamp: string | null; date: string; confidence: 'exact' | 'date_only' } | null | undefined): string {
  if (!generatedAt) return "\u2014";
  if (generatedAt.confidence === 'exact' && generatedAt.timestamp) {
    return format(new Date(generatedAt.timestamp), "MMM d, yyyy 'at' h:mm a");
  }
  try {
    const dateStr = generatedAt.date + "T00:00:00";
    return format(new Date(dateStr), "MMM d, yyyy") + ", time unknown";
  } catch {
    return generatedAt.date + ", time unknown";
  }
}


function LocationDetailPanel({
  locationCode,
  locationLabel,
  lastReportDate,
  locationData,
  capacity,
  locationMetrics,
  onViewAnimalPreview,
  onAddAnimalTo,
  allLocations,
  onSelectLocation,
  defaultLocationCode,
  canToggleLocationScope,
  showAllLocations,
  onToggleAllLocations,
  sortedLocationsCount,
  allCapacity,
}: LocationDetailPanelProps) {
  const [activeTab, setActiveTab] = useState("overview");
  const isMobile = useIsMobile();

  // Task #99: Use generatedAt from metrics byLocation (preferred), fall back to freshness hook
  const { allPets } = useReportFreshness();
  const locationFreshnessEntry = allPets.locations.find(l => l.locationCode === locationCode);
  const generatedAt = locationData?.generatedAt ?? locationFreshnessEntry?.generatedAt ?? null;

  const { data: losData, isLoading: losLoading, isError: losError } = useLengthOfStay(
    365,
    locationCode,
    undefined,
  );

  const losCurrentData = losData?.currentInShelter?.all ?? null;

  const { data: losAnimalsData, isLoading: losAnimalsLoading } = useQuery<{ animals: AnimalListItem[] }>({
    queryKey: ["/api/animals", "longest-stay", locationCode],
    queryFn: async () => {
      const response = await fetch(`/api/animals?location=${encodeURIComponent(locationCode)}&currentOnly=true`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch animals");
      return response.json();
    },
    enabled: activeTab === "longest-stay",
  });

  const longestStayAnimals = (losAnimalsData?.animals ?? [])
    .filter((a: AnimalListItem) => a.intakeDate)
    .sort((a: AnimalListItem, b: AnimalListItem) => {
      const daysA = computeDaysInShelter(a.intakeDate);
      const daysB = computeDaysInShelter(b.intakeDate);
      return (daysB ?? 0) - (daysA ?? 0);
    })
    .slice(0, 15);

  const { data: adoptionData, isLoading: adoptionLoading, isError: adoptionError } = useQuery<{ latest: AdoptionPipelineLatest | null }>({
    queryKey: ["/api/population/adoption-pipeline", locationCode],
    queryFn: async () => {
      const response = await fetch(`/api/population/adoption-pipeline?location=${encodeURIComponent(locationCode)}&days=90`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch adoption pipeline");
      return response.json();
    },
    enabled: activeTab === "adoption-levels" || activeTab === "overview",
  });

  const { data: intakeOutcomeData, isLoading: intakeOutcomeLoading, isError: intakeOutcomeError } = useQuery<{ trends: IntakeOutcomeTrend[] }>({
    queryKey: ["/api/population/intake-outcome-trends", locationCode],
    queryFn: async () => {
      const response = await fetch(`/api/population/intake-outcome-trends?location=${encodeURIComponent(locationCode)}&days=90`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch intake/outcome");
      const raw = await response.json();
      return {
        trends: (raw.series ?? []).map((t: { periodStart: string; intakes: number; outcomes: number }) => ({
          date: t.periodStart,
          intakes: t.intakes,
          outcomes: t.outcomes,
        })),
      };
    },
    enabled: activeTab === "intake-outcome",
  });

  return (
    <Card className="border-primary/30 shadow-sm overflow-visible" data-testid={`panel-location-detail-${locationCode}`}>
      <div className="px-4 py-2.5 bg-primary/5 border-b border-primary/10">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <DropdownMenu>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-tight flex-shrink-0 hidden sm:inline">Location:</span>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 font-bold text-sm uppercase tracking-tight ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer hover-elevate min-w-0"
                    data-testid={`button-location-switcher-${locationCode}`}
                  >
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground sm:hidden" />
                    <span className="truncate">{locationLabel}</span>
                    <ChevronsUpDown className="h-3 w-3 flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
              </div>
              <DropdownMenuContent align="start" className="w-64">
                <div className="flex items-center justify-between gap-2 px-2 py-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="h-3.5 w-3.5 flex-shrink-0 inline-block" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Location</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <span>Pop.</span>
                    <span>Cap.</span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {allLocations.map((loc) => {
                  const isSelected = loc.location === locationCode;
                  const isDefault = loc.location === defaultLocationCode;
                  const locCap = allCapacity[loc.location];
                  const utilPct = locCap?.utilizationConfigured ?? null;
                  return (
                    <DropdownMenuItem
                      key={loc.location}
                      onClick={() => onSelectLocation(loc.location)}
                      className="flex items-center justify-between gap-2"
                      data-testid={`menu-item-location-${loc.location}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isSelected ? (
                          <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                        ) : (
                          <span className="h-3.5 w-3.5 flex-shrink-0 inline-block" />
                        )}
                        <span className="truncate font-medium">{loc.locationLabel}</span>
                        {isDefault && <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 text-xs text-muted-foreground">
                        <span>{loc.total}</span>
                        {utilPct !== null && <span>{utilPct}%</span>}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
                {canToggleLocationScope && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={onToggleAllLocations}
                      onSelect={e => e.preventDefault()}
                      data-testid="menu-item-toggle-location-scope"
                    >
                      {showAllLocations ? "Show my locations only" : `View all ${sortedLocationsCount} locations`}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Desktop only: badge stays next to title in the same row */}
            <Badge variant="outline" className="text-[10px] hidden sm:inline-flex" data-testid={`badge-snapshot-updated-${locationCode}`}>
              {generatedAt ? `Updated ${formatGeneratedAtBadge(generatedAt)}` : `Updated ${formatReportDate(lastReportDate)}`}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mr-1 hidden sm:inline">Quick Links:</span>
          {([
            { href: `/animals?location=${locationCode}`, icon: PawPrint, label: `${locationLabel} Animals`, testId: `button-animals-${locationCode}` },
            { href: `/interactions?locationCode=${locationCode}`, icon: MessageSquare, label: `${locationLabel} Interactions`, testId: `button-interactions-${locationCode}` },
            { href: `/population?shelter=${locationCode}`, icon: BarChart3, label: `${locationLabel} Population`, testId: `button-population-${locationCode}` },
            { href: `/behavior?tab=workflow&shelter=${locationCode}`, icon: Award, label: `${locationLabel} Behavior`, testId: `button-behavior-${locationCode}` },
          ] as const).map(({ href, icon: Icon, label, testId }) => {
            const btn = (
              <Button variant="ghost" size="icon" asChild key={testId}>
                <Link href={href} data-testid={testId}>
                  <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                </Link>
              </Button>
            );
            if (isMobile) return btn;
            return (
              <Tooltip key={testId}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            );
          })}
          </div>
        </div>
        {/* Mobile only: timestamp on its own dedicated row below title+icons */}
        <Badge variant="outline" className="text-[10px] sm:hidden mt-1" data-testid={`badge-snapshot-updated-mobile-${locationCode}`}>
          {generatedAt ? `Updated ${formatGeneratedAtBadge(generatedAt)}` : `Updated ${formatReportDate(lastReportDate)}`}
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b px-4 overflow-x-auto">
          <TabsList className="h-9 bg-transparent p-0 gap-4 w-max sm:w-auto">
            <TabsTrigger
              value="overview"
              className="h-9 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 text-xs font-medium"
              data-testid={`tab-overview-${locationCode}`}
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="longest-stay"
              className="h-9 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 text-xs font-medium"
              data-testid={`tab-longest-stay-${locationCode}`}
            >
              Length of Stay
            </TabsTrigger>
            <TabsTrigger
              value="adoption-levels"
              className="h-9 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 text-xs font-medium"
              data-testid={`tab-adoption-levels-${locationCode}`}
            >
              Adoption Levels
            </TabsTrigger>
            <TabsTrigger
              value="intake-outcome"
              className="h-9 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 text-xs font-medium"
              data-testid={`tab-intake-outcome-${locationCode}`}
            >
              Intakes & Outcomes
            </TabsTrigger>
            {locationMetrics.map((m) => (
              <TabsTrigger
                key={m.id}
                value={`metric-${m.id}`}
                className="h-9 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-1 text-xs font-medium flex items-center gap-1"
                data-testid={`tab-metric-${m.id}-${locationCode}`}
              >
                <BarChart2 className="h-3 w-3 flex-shrink-0" />
                {m.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="overview" className="m-0 p-4">
          {adoptionLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="rounded-md border p-3 flex flex-col items-center gap-2">
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-3 w-20" />
                </div>
              ))}
              <div className="col-span-2 lg:col-span-4 rounded-md border p-3">
                <Skeleton className="h-3 w-24 mb-2" />
                <Skeleton className="h-4 w-full rounded-full" />
                <div className="flex justify-between mt-2">
                  {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-3 w-12" />)}
                </div>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid={`overview-stats-${locationCode}`}>
            {(() => {
              const dogs = locationData?.dogs ?? 0;
              const cats = locationData?.cats ?? 0;
              const total = locationData?.total ?? 0;
              const utilization = capacity?.utilizationConfigured ?? null;
              const capStatus = capacity?.status ?? "unknown";
              const adoptionLatest = adoptionData?.latest;
              const adoptionTotal = adoptionLatest ? adoptionLatest.L1 + adoptionLatest.L2 + adoptionLatest.L3 + adoptionLatest.L4 : 0;

              return (
                <>
                  <div className="rounded-md border p-3 text-center">
                    <p className="text-2xl font-medium text-foreground/85 dark:text-foreground" data-testid={`overview-total-${locationCode}`}>{total}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Total Animals</p>
                  </div>

                  <div className="rounded-md border p-3 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <div>
                        <p className="text-xl font-medium text-foreground/85 dark:text-foreground" data-testid={`overview-dogs-${locationCode}`}>{dogs}</p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Dogs</p>
                      </div>
                      <span className="text-muted-foreground/30 text-lg">/</span>
                      <div>
                        <p className="text-xl font-medium text-foreground/85 dark:text-foreground" data-testid={`overview-cats-${locationCode}`}>{cats}</p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Cats</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border p-3 text-center">
                    {utilization !== null ? (
                      <>
                        <p className={`text-2xl font-medium ${
                          capStatus === "over" ? "text-destructive"
                            : capStatus === "near" ? "text-amber-500"
                            : capStatus === "ok" ? "text-emerald-500"
                            : ""
                        }`} data-testid={`overview-capacity-${locationCode}`}>
                          {utilization}%
                        </p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Capacity</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xl font-medium text-muted-foreground/40">—</p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Capacity</p>
                      </>
                    )}
                  </div>

                  <div className="rounded-md border p-3 text-center">
                    <p className={`text-2xl font-medium ${(adoptionData?.latest?.L4 ?? 0) > 0 ? "text-destructive" : "text-foreground/85 dark:text-foreground"}`} data-testid={`overview-l4-${locationCode}`}>
                      {adoptionData?.latest?.L4 ?? 0}
                    </p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Level 4</p>
                  </div>

                  {adoptionLatest && adoptionTotal > 0 && (
                    <div className="col-span-2 lg:col-span-4 rounded-md border p-3">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 font-medium">Adoption Levels</p>
                      <div className="flex h-4 rounded-full overflow-hidden">
                        {adoptionLatest.L1 > 0 && (
                          <div
                            className="bg-emerald-400/40 dark:bg-emerald-500/40 transition-all"
                            style={{ width: `${(adoptionLatest.L1 / adoptionTotal) * 100}%` }}
                            title={`L1: ${adoptionLatest.L1}`}
                          />
                        )}
                        {adoptionLatest.L2 > 0 && (
                          <div
                            className="bg-blue-400/40 dark:bg-blue-500/40 transition-all"
                            style={{ width: `${(adoptionLatest.L2 / adoptionTotal) * 100}%` }}
                            title={`L2: ${adoptionLatest.L2}`}
                          />
                        )}
                        {adoptionLatest.L3 > 0 && (
                          <div
                            className="bg-amber-400/40 dark:bg-amber-500/40 transition-all"
                            style={{ width: `${(adoptionLatest.L3 / adoptionTotal) * 100}%` }}
                            title={`L3: ${adoptionLatest.L3}`}
                          />
                        )}
                        {adoptionLatest.L4 > 0 && (
                          <div
                            className="bg-red-400/40 dark:bg-red-500/40 transition-all"
                            style={{ width: `${(adoptionLatest.L4 / adoptionTotal) * 100}%` }}
                            title={`L4: ${adoptionLatest.L4}`}
                          />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-2 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400/40 dark:bg-emerald-500/40 inline-block" />L1: {adoptionLatest.L1}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400/40 dark:bg-blue-500/40 inline-block" />L2: {adoptionLatest.L2}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400/40 dark:bg-amber-500/40 inline-block" />L3: {adoptionLatest.L3}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400/40 dark:bg-red-500/40 inline-block" />L4: {adoptionLatest.L4}</span>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          )}
        </TabsContent>

        <TabsContent value="longest-stay" className="m-0 p-4">
          {losLoading ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col p-3 rounded-md border gap-1">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-[200px] w-full" />
            </div>
          ) : losError ? (
            <div className="p-6 text-center text-muted-foreground" data-testid={`text-longest-stay-error-${locationCode}`}>
              <p className="text-sm">Failed to load length of stay data</p>
            </div>
          ) : !losCurrentData || losCurrentData.totalAnimals === 0 ? (
            <div className="p-6 text-center text-muted-foreground" data-testid={`text-no-longest-stay-${locationCode}`}>
              <p className="text-sm">No current stay data available</p>
            </div>
          ) : (
            <div data-testid={`los-chart-${locationCode}`}>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 font-medium">Currently In Shelter</p>
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex flex-col p-3 rounded-md border" data-testid={`stat-los-avg-${locationCode}`}>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Average</span>
                  <span className="text-lg font-semibold">{losCurrentData.averageDays} days</span>
                </div>
                <div className="flex flex-col p-3 rounded-md border" data-testid={`stat-los-median-${locationCode}`}>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Median</span>
                  <span className="text-lg font-semibold">{losCurrentData.medianDays} days</span>
                </div>
                <div className="flex flex-col p-3 rounded-md border" data-testid={`stat-los-total-${locationCode}`}>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Animals</span>
                  <span className="text-lg font-semibold">{losCurrentData.totalAnimals}</span>
                </div>
              </div>
              {losCurrentData.buckets.length > 0 && (
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={losCurrentData.buckets.map((b) => ({
                        label: b.label,
                        count: b.count,
                        pct: losCurrentData.totalAnimals > 0 ? Math.round((b.count / losCurrentData.totalAnimals) * 100) : 0,
                      }))}
                      layout="vertical"
                      margin={{ left: 10, right: 20, top: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="label" width={75} tick={{ fontSize: 11 }} />
                      <RechartsTooltip
                        formatter={(value: number, _name: string, props: { payload?: { pct?: number } }) => [
                          `${value} animals (${props.payload?.pct ?? 0}%)`,
                          "Current",
                        ]}
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          borderColor: "hsl(var(--border))",
                          borderRadius: "6px",
                          color: "hsl(var(--card-foreground))",
                        }}
                      />
                      <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="mt-4 border-t pt-3">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2 font-medium">Top 15 — Longest Current Stays</p>
                {losAnimalsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-7 w-7 rounded-full" />
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-4 w-12 ml-auto" />
                      </div>
                    ))}
                  </div>
                ) : longestStayAnimals.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-3">No animals with intake dates found</p>
                ) : (
                  <Table className="table-zebra">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs w-10">Pet</TableHead>
                        <TableHead className="text-xs">Name / ID</TableHead>
                        <TableHead className="text-xs text-center">Level</TableHead>
                        <TableHead className="text-xs text-right">Days</TableHead>
                        <TableHead className="text-xs w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {longestStayAnimals.map((animal: AnimalListItem) => {
                        const days = computeDaysInShelter(animal.intakeDate);
                        return (
                          <TableRow key={animal.animalId} className="cursor-pointer" data-testid={`row-longest-stay-${animal.animalId}`}>
                            <TableCell className="py-1.5">
                              <AnimalAvatar
                                animalId={animal.animalId}
                                photoUrl={animal.photoUrl}
                                name={animal.name}
                                species={animal.species}
                                className="h-7 w-7"
                              />
                            </TableCell>
                            <TableCell className="py-1.5">
                              <span className="text-sm font-medium">{animal.name}</span>
                              <span className="text-[10px] text-muted-foreground/60 ml-2 font-mono">{animal.animalId}</span>
                            </TableCell>
                            <TableCell className="py-1.5 text-center">
                              <Badge variant="outline" className="text-[10px] font-mono">
                                {animal.adoptionLevelNorm || "—"}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-1.5 text-right">
                              {days !== null ? (
                                <span className={`text-sm font-semibold ${days > 90 ? "text-destructive" : days > 60 ? "text-amber-600 dark:text-amber-500" : "text-muted-foreground"}`}>
                                  {days}d
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="py-1.5">
                              <AnimalActionMenu
                                animalId={animal.animalId}
                                animalName={animal.name}
                                animalSpecies={animal.species}
                                animalPhotoUrl={animal.photoUrl}
                                onViewPreview={onViewAnimalPreview}
                                onAddTo={onAddAnimalTo}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="adoption-levels" className="m-0 p-4">
          {adoptionLoading ? (
            <div className="space-y-3 max-w-md">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-5 w-full" />
              ))}
            </div>
          ) : adoptionError ? (
            <div className="text-center text-muted-foreground py-4" data-testid={`text-adoption-error-${locationCode}`}>
              <p className="text-sm">Failed to load adoption level data</p>
            </div>
          ) : !adoptionData?.latest ? (
            <div className="text-center text-muted-foreground py-4" data-testid={`text-no-adoption-${locationCode}`}>
              <p className="text-sm">No adoption level data available</p>
            </div>
          ) : (() => {
            const latest = adoptionData.latest;
            const total = latest.L1 + latest.L2 + latest.L3 + latest.L4;
            return (
              <>
                <div className="space-y-3 max-w-md">
                  <AdoptionLevelBar level="L1" count={latest.L1} total={total} color="bg-emerald-400 dark:bg-emerald-500" />
                  <AdoptionLevelBar level="L2" count={latest.L2} total={total} color="bg-blue-400 dark:bg-blue-500" />
                  <AdoptionLevelBar level="L3" count={latest.L3} total={total} color="bg-amber-400 dark:bg-amber-500" />
                  <AdoptionLevelBar level="L4" count={latest.L4} total={total} color="bg-red-400 dark:bg-red-500" />
                </div>
                <div className="mt-4 pt-3 border-t">
                  <Button variant="ghost" size="sm" className="text-xs p-0 h-auto text-primary" asChild>
                    <a
                      href={`/population?shelter=${locationCode}#section-population-breakdowns`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`link-adoption-levels-${locationCode}`}
                    >
                      View Population Breakdown <ExternalLink className="ml-1.5 h-3 w-3" />
                    </a>
                  </Button>
                </div>
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="intake-outcome" className="m-0 p-4">
          {intakeOutcomeLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : intakeOutcomeError ? (
            <div className="text-center text-muted-foreground py-4" data-testid={`text-intake-outcome-error-${locationCode}`}>
              <p className="text-sm">Failed to load intake/outcome data</p>
            </div>
          ) : !intakeOutcomeData?.trends || intakeOutcomeData.trends.length === 0 ? (
            <div className="text-center text-muted-foreground py-4" data-testid={`text-no-intake-outcome-${locationCode}`}>
              <p className="text-sm">No intake/outcome data for this period</p>
            </div>
          ) : (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={intakeOutcomeData.trends.map(t => ({
                    ...t,
                    label: format(new Date(t.date + "T00:00:00"), "M/d"),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="intakes" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} name="Intakes" />
                    <Line type="monotone" dataKey="outcomes" stroke="hsl(142 71% 45%)" strokeWidth={2} dot={{ r: 3 }} name="Outcomes" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 pt-3 border-t">
                <Button variant="ghost" size="sm" className="text-xs p-0 h-auto text-primary" asChild>
                  <a
                    href={`/population?shelter=${locationCode}&tab=insights#section-intakes-outcomes`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`link-population-insights-${locationCode}`}
                  >
                    View Intakes & Outcomes <ExternalLink className="ml-1.5 h-3 w-3" />
                  </a>
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {locationMetrics.map((m) => (
          <TabsContent key={m.id} value={`metric-${m.id}`} className="m-0 p-4" data-testid={`tab-content-metric-${m.id}-${locationCode}`}>
            <MetricWidget
              metricId={m.id}
              label={m.label}
              description={m.description}
              locationOverride={locationCode}
              displayFields={m.displayFields ?? []}
              defaultSortField={m.defaultSortField}
              defaultSortDirection={m.defaultSortDirection}
              chartEnabled={m.chartEnabled}
              defaultView={m.defaultView}
              resultType={m.resultType}
            />
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
}
